import { useState, useEffect } from 'react'
import { Dialog, DialogPortal } from '@/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { DatePickerCalendar } from '@/components/ui/DatePickerCalendar'
import { Calendar, X } from 'lucide-react'
import { format, setHours, setMinutes } from 'date-fns'
import { cn } from '@/lib/utils'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { CREATE_BOOKING_ENDPOINT } from '@/config/endpoints'
import { addBookingConfirmationMessageToChat } from '@/lib/chatData'

interface BookingAvailabilityDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSendAvailability?: (estimatedTime: number | 'unknown', earliestDate: Date, preparationDays?: number) => void
  onRecordBooking?: (bookingDate: Date, bookingTime?: string) => void
  chatId?: string
  artistId?: string
  onBookingCreated?: () => void
}

// Generate time slots: 9 AM to 9 PM in 30-minute increments
const generateTimeSlots = (): string[] => {
  const slots: string[] = []
  for (let hour = 9; hour < 21; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      slots.push(time)
    }
  }
  return slots
}

const TIME_SLOTS = generateTimeSlots()

export function BookingAvailabilityDialog({
  isOpen,
  onOpenChange,
  onSendAvailability,
  onRecordBooking,
  chatId,
  artistId,
  onBookingCreated
}: BookingAvailabilityDialogProps) {
  const { user } = useAuthState()
  const { userData } = useUserData()
  const [mode, setMode] = useState<'offer' | 'record'>('offer')
  const [estimatedTime, setEstimatedTime] = useState<string>('1')
  const [preparationDays, setPreparationDays] = useState<string>('7')
  const [showCalendar, setShowCalendar] = useState(false)
  const [bookingDate, setBookingDate] = useState<Date>(new Date())
  const [bookingTime, setBookingTime] = useState<string>('09:00')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recordError, setRecordError] = useState<string | null>(null)

  // Reset when dialog opens
  useEffect(() => {
    if (!isOpen) {
      setMode('offer')
      setEstimatedTime('1')
      setPreparationDays('7')
      setShowCalendar(false)
      setBookingDate(new Date())
      setBookingTime('09:00')
      setIsSubmitting(false)
      setRecordError(null)
    }
  }, [isOpen])

  const handleSendAvailability = () => {
    const timeValue = estimatedTime === 'unknown' ? 'unknown' : parseFloat(estimatedTime)
    const prepDays = parseInt(preparationDays)
    onSendAvailability?.(timeValue, new Date(), prepDays)
    onOpenChange(false)
  }

  const handleConfirmBooking = async () => {
    console.log('[BookingAvailabilityDialog:confirmBooking] Button clicked')
    console.log('[BookingAvailabilityDialog:confirmBooking] Current state:', {
      bookingDate,
      bookingTime,
      user: !!user,
      chatId,
      artistId
    })

    if (!bookingDate || !bookingTime || !user) {
      console.log('[BookingAvailabilityDialog:confirmBooking] Missing basic information')
      setRecordError('Missing required booking information')
      return
    }

    // If chatId and artistId are not provided, just call the callback without API
    if (!chatId || !artistId) {
      console.log('[BookingAvailabilityDialog:confirmBooking] No chatId/artistId, calling callback without API')
      onRecordBooking?.(bookingDate, bookingTime)
      onOpenChange(false)
      return
    }

    setIsSubmitting(true)
    setRecordError(null)

    try {
      // Parse the booking time (format: "HH:MM") and combine with booking date
      const [hours, minutes] = bookingTime.split(':').map(Number)
      const bookingDateTime = setMinutes(setHours(bookingDate, hours), minutes)
      const bookingTimestamp = bookingDateTime.getTime() // Unix timestamp in milliseconds

      console.log('[BookingAvailabilityDialog:confirmBooking] 🎫 Starting booking confirmation flow')
      console.log('[BookingAvailabilityDialog:confirmBooking] Selection details:', {
        bookingDate: bookingDate.toISOString(),
        bookingTime: bookingTime,
        bookingDateTime: bookingDateTime.toISOString(),
        bookingTimestamp,
        user: user.email,
        artistId: artistId,
        chatId: chatId
      })

      // STEP 1: Create the confirmation message FIRST to get its messageId
      let confirmationMessageId: string | undefined
      if (chatId && user) {
        try {
          const finalEstimatedTimeForMessage = estimatedTime === 'unknown' ? 'unknown' : parseFloat(estimatedTime)
          confirmationMessageId = await addBookingConfirmationMessageToChat(
            chatId,
            bookingDate,
            0, // No price range in this dialog
            0, // No price range in this dialog
            finalEstimatedTimeForMessage,
            user.uid,
            'artist',
            bookingTime
          )
          console.log('[BookingAvailabilityDialog:confirmBooking] ✅ Booking confirmation message added to chat with ID:', confirmationMessageId)
        } catch (msgErr) {
          console.error('[BookingAvailabilityDialog:confirmBooking] ❌ Failed to create confirmation message:', msgErr)
          throw new Error('Failed to create booking confirmation message')
        }
      }

      // STEP 2: Create the booking with the messageId
      const finalEstimatedTime = estimatedTime === 'unknown' ? 1 : parseFloat(estimatedTime)
      
      const clientData: Record<string, string | boolean> = {}
      
      // Add auto-populated fields
      if (user?.email) clientData.email = user.email
      if (userData?.instagram) clientData.username = userData.instagram
      
      const requestData = {
        chatId: chatId,
        clientData: clientData,
        timestamp: bookingTimestamp,
        bookingDateTime: bookingDateTime.toISOString(),
        artistId: artistId,
        estimatedTime: finalEstimatedTime,
        messageId: confirmationMessageId // Link booking to the confirmation message
      }

      console.log('[BookingAvailabilityDialog:confirmBooking] Booking request payload:', requestData)

      // Call the create_booking endpoint
      console.log('[BookingAvailabilityDialog:confirmBooking] Sending booking creation request to backend')
      const response = await fetch(CREATE_BOOKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      console.log('[BookingAvailabilityDialog:confirmBooking] Backend response:', {
        status: response.status,
        statusText: response.statusText
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[BookingAvailabilityDialog:confirmBooking] ❌ Backend error:', errorData)
        throw new Error(errorData.message || `Failed to create booking: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('[BookingAvailabilityDialog:confirmBooking] ✅ Booking created in backend')
      console.log('[BookingAvailabilityDialog:confirmBooking] Backend response:', result)

      // Notify callbacks
      if (onBookingCreated) {
        console.log('[BookingAvailabilityDialog:confirmBooking] Calling onBookingCreated callback')
        onBookingCreated()
      }
      
      onRecordBooking?.(bookingDate, bookingTime)
      
      console.log('[BookingAvailabilityDialog:confirmBooking] ✅ Booking confirmation flow complete!')
      onOpenChange(false)
    } catch (err) {
      console.error('[BookingAvailabilityDialog:confirmBooking] ❌ Error creating booking:', err)
      console.error('[BookingAvailabilityDialog:confirmBooking] Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err)
      })
      setRecordError(err instanceof Error ? err.message : 'Failed to create booking. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
            "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {mode === 'offer' ? 'Share Available Times' : 'Record Booking'}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="p-2 -mr-2"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-100px)]">
            <div className="space-y-5">
              {mode === 'offer' ? (
                <>
                  {/* Estimated Time Picker */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Estimated time for this session?
                    </Label>
                    <div className="border border-input rounded-md">
                      <select
                        value={estimatedTime}
                        onChange={(e) => setEstimatedTime(e.target.value)}
                        className="w-full px-4 py-2 text-sm bg-background text-foreground border-0 rounded-[inherit] focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2rem'
                        }}
                      >
                        <option value="unknown">Unknown</option>
                        {Array.from({ length: 48 }, (_, i) => {
                          const hours = (i + 2) * 0.5
                          return (
                            <option key={hours} value={hours.toString()}>
                              {hours % 1 === 0 ? `${hours.toFixed(0)}h` : `${hours}h`}
                            </option>
                          )
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Preparation Days Picker */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium text-foreground">
                        How much time do you need to prepare?
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Starting from the time it's booked
                      </p>
                    </div>
                    <div className="border border-input rounded-md">
                      <select
                        value={preparationDays}
                        onChange={(e) => setPreparationDays(e.target.value)}
                        className="w-full px-4 py-2 text-sm bg-background text-foreground border-0 rounded-[inherit] focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2rem'
                        }}
                      >
                        <option value="1">1 day</option>
                        <option value="2">2 days</option>
                        <option value="3">3 days</option>
                        <option value="4">4 days</option>
                        <option value="5">5 days</option>
                        <option value="6">6 days</option>
                        <option value="7">1 week</option>
                        <option value="8">8 days</option>
                        <option value="9">9 days</option>
                        <option value="10">10 days</option>
                        <option value="11">11 days</option>
                        <option value="12">12 days</option>
                        <option value="13">13 days</option>
                        <option value="14">2 weeks</option>
                        <option value="21">3 weeks</option>
                        <option value="30">1 month</option>
                        <option value="60">2 months</option>
                        <option value="90">3 months</option>
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <Button
                      onClick={handleSendAvailability}
                      className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
                      size="lg"
                    >
                      Send to Client
                    </Button>
                    <div className="w-full border border-input rounded-[var(--card-radius)]">
                      <Button
                        variant="outline"
                        onClick={() => setMode('record')}
                        className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] border-0"
                        size="lg"
                      >
                        Time Already Agreed?
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Estimated Time Picker */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Estimated time for this session?
                    </Label>
                    <div className="border border-input rounded-md">
                      <select
                        value={estimatedTime}
                        onChange={(e) => setEstimatedTime(e.target.value)}
                        className="w-full px-4 py-2 text-sm bg-background text-foreground border-0 rounded-[inherit] focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2rem'
                        }}
                      >
                        <option value="unknown">Unknown</option>
                        {Array.from({ length: 48 }, (_, i) => {
                          const hours = (i + 2) * 0.5
                          return (
                            <option key={hours} value={hours.toString()}>
                              {hours % 1 === 0 ? `${hours.toFixed(0)}h` : `${hours}h`}
                            </option>
                          )
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Booking Date Picker */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Select booking date
                    </Label>
                    <div className="border border-input rounded-md">
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal border-0 rounded-[inherit]"
                        onClick={() => setShowCalendar(true)}
                        type="button"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {format(bookingDate, 'PPP')}
                      </Button>
                    </div>
                  </div>

                  {/* Booking Time Picker */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Select booking time
                    </Label>
                    <div className="border border-input rounded-md">
                      <select
                        value={bookingTime}
                        onChange={(e) => setBookingTime(e.target.value)}
                        className="w-full px-4 py-2 text-sm bg-background text-foreground border-0 rounded-[inherit] focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2rem'
                        }}
                      >
                        {TIME_SLOTS.map(slot => (
                          <option key={slot} value={slot}>
                            {slot}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Error Message */}
                  {recordError && (
                    <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md">
                      <p className="text-xs text-destructive">{recordError}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <Button
                      onClick={handleConfirmBooking}
                      disabled={isSubmitting}
                      className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
                      size="lg"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Confirming...
                        </span>
                      ) : (
                        'Confirm'
                      )}
                    </Button>
                    <div className="w-full border border-input rounded-[var(--card-radius)]">
                      <Button
                        variant="outline"
                        onClick={() => setMode('offer')}
                        disabled={isSubmitting}
                        className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] border-0"
                        size="lg"
                      >
                        Back
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>

      {/* Calendar Popup Dialog */}
      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[70] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-[70] translate-x-[-50%] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] p-0 overflow-hidden",
              "w-[90vw] max-w-[400px]"
            )}
          >
            {/* Calendar Content */}
            <div className="p-4">
              <DatePickerCalendar
                value={bookingDate}
                onChange={setBookingDate}
                onCancel={() => setShowCalendar(false)}
                onSave={() => setShowCalendar(false)}
                minDate={new Date()}
                title="Select Booking Date"
              />
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </Dialog>
  )
}

